import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import UserScheduleEditor from '../components/admin/UserScheduleEditor';
import workScheduleService, {
  type OrgDaySchedule,
  DAY_NAMES
} from '../services/workScheduleService';
import organizationService from '../services/organization';
import orgPolicyService from '../services/orgPolicyService';
import userService from '../services/user';
import { useOrgTimezone, formatDateTimeForInput, formatLocalDateInTz } from '../utils/timezone';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { Clock } from 'lucide-react';

const PAY_CYCLE_DAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday (default)' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

const EMPTY_ORG_SCHEDULE: OrgDaySchedule[] = Array.from({ length: 7 }, (_, i) => ({
  day_of_week: i + 1,
  start_time: '07:00',
  end_time: '15:30',
  lunch_duration_minutes: 30,
  lunch_start_time: null,
  is_working_day: i + 1 <= 5, // Mon–Fri = working
}));

interface UserScheduleTarget {
  userId: number;
  userName: string;
}

export default function WorkScheduleSettings() {
  const { user } = useAuth();
  const orgId = user?.organisation_id;
  const orgTimezone = useOrgTimezone();
  const { hasAccess: hasHRAccess, loading: hrLoading } = useModuleLicense('timesheet', 'hr');

  // Org schedule state
  const [orgSchedule, setOrgSchedule] = useState<OrgDaySchedule[]>(EMPTY_ORG_SCHEDULE);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [orgSuccess, setOrgSuccess] = useState<string | null>(null);

  // Org settings (pay cycle + grace window)
  const [payCycleStartDay, setPayCycleStartDay] = useState(4);
  const [graceMinutes, setGraceMinutes] = useState(15);
  const [fortnightlyRefDate, setFortnightlyRefDate] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);

  // User schedule override state — the editor itself is the reusable
  // UserScheduleEditor component; this page only owns the employee picker.
  const [users, setUsers] = useState<{ id: number; username: string; first_name?: string; last_name?: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserScheduleTarget | null>(null);

  // Load org schedule and settings
  useEffect(() => {
    if (!orgId) return;
    loadOrgSchedule();
    loadOrgSettings();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadOrgSchedule = async () => {
    if (!orgId) return;
    setOrgLoading(true);
    try {
      const response = await workScheduleService.getOrgSchedule(orgId);
      if (response.success && response.data && response.data.length > 0) {
        // Merge fetched days with empty defaults (in case some days aren't in the DB yet)
        const merged = EMPTY_ORG_SCHEDULE.map(empty => {
          const fetched = response.data!.find(d => d.day_of_week === empty.day_of_week);
          return fetched || empty;
        });
        setOrgSchedule(merged);
      }
    } catch {
      setOrgError('Failed to load organisation schedule');
    } finally {
      setOrgLoading(false);
    }
  };

  const loadOrgSettings = async () => {
    try {
      const response = await organizationService.getCurrentOrganization();
      if (response.success && response.data) {
        setPayCycleStartDay((response.data as any).pay_cycle_start_day ?? 4);
        setGraceMinutes((response.data as any).clock_grace_minutes ?? 15);
        const rawDate = (response.data as any).fortnightly_cycle_reference_date;
        if (rawDate) {
          // postgres-date parses DATE columns as local-midnight JS Dates; after JSON
          // serialisation they arrive as UTC ISO strings (e.g. "2026-03-24T13:00:00Z"
          // for a March-25 date on a UTC+11 server). formatDateTimeForInput converts
          // the UTC instant back to the correct calendar date in the org timezone.
          setFortnightlyRefDate(formatDateTimeForInput(rawDate, orgTimezone).slice(0, 10));
        } else {
          setFortnightlyRefDate('');
        }
      }
    } catch {
      // ignore — defaults are fine
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      if (response.success && response.data) {
        setUsers(response.data.map(u => ({ ...u, first_name: u.first_name ?? undefined, last_name: u.last_name ?? undefined })));
      }
    } catch {
      // ignore
    }
  };

  const handleUserSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = parseInt(e.target.value);
    if (!userId) {
      setSelectedUser(null);
      return;
    }
    const u = users.find(u => u.id === userId);
    if (u) {
      const name = u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username;
      setSelectedUser({ userId, userName: name });
    }
  };

  const updateOrgDay = (dayOfWeek: number, field: keyof OrgDaySchedule, value: string | number | boolean | null) => {
    setOrgSchedule(prev => prev.map(d =>
      d.day_of_week === dayOfWeek ? { ...d, [field]: value } : d
    ));
  };

  const saveOrgSchedule = async () => {
    if (!orgId) return;
    setOrgSaving(true);
    setOrgError(null);
    setOrgSuccess(null);
    try {
      const response = await workScheduleService.updateOrgSchedule(orgId, orgSchedule);
      if (response.success) {
        const reissued = orgPolicyService.reissuedMessage(response);
        setOrgSuccess(reissued ? `Organisation schedule saved. ${reissued}` : 'Organisation schedule saved successfully');
        setTimeout(() => setOrgSuccess(null), reissued ? 10000 : 3000);
      } else {
        setOrgError((response as any).error?.message || 'Failed to save');
      }
    } catch {
      setOrgError('Failed to save organisation schedule');
    } finally {
      setOrgSaving(false);
    }
  };

  const saveOrgSettings = async () => {
    if (!orgId) return;
    setSettingsSaving(true);
    setSettingsError(null);
    setSettingsSuccess(null);
    try {
      const response = await organizationService.update(orgId, {
        pay_cycle_start_day: payCycleStartDay,
        clock_grace_minutes: graceMinutes,
        fortnightly_cycle_reference_date: fortnightlyRefDate || null,
      } as any);
      if (response.success) {
        setSettingsSuccess('Settings saved successfully');
        setTimeout(() => setSettingsSuccess(null), 3000);
      } else {
        setSettingsError((response as any).error?.message || 'Failed to save');
      }
    } catch {
      setSettingsError('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  if (hrLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (!hasHRAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Clock className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR license seat is required to access work schedule settings.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Work Schedule Settings" />
      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Pay Cycle & Grace Window */}
        <Card>
          <CardHeader>
            <CardTitle>Pay Cycle & Clock Grace Window</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pay Cycle Start Day
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={payCycleStartDay}
                  onChange={e => setPayCycleStartDay(parseInt(e.target.value))}
                >
                  {PAY_CYCLE_DAYS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The overtime threshold resets on this day for weekly-paid staff (38 h/week). Fortnightly-paid staff have a 76 h/fortnight threshold instead.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clock Grace Window (minutes)
                </label>
                <input
                  type="number"
                  min={0}
                  max={60}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={graceMinutes}
                  onChange={e => setGraceMinutes(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Clock-in grace: clocking in up to this many minutes <strong>before</strong> the
                  scheduled start will snap the recorded time forward to the scheduled start.
                  Clocking in after the scheduled start records the actual time (no backdating).
                  Clock-out grace: clocking out up to this many minutes <strong>after</strong> the
                  scheduled end will snap the recorded time back to the scheduled end.
                  Clocking out before the scheduled end records the actual time (no rounding up).
                </p>
              </div>
            </div>
            {settingsError && <p className="mt-3 text-sm text-red-600">{settingsError}</p>}
            {settingsSuccess && <p className="mt-3 text-sm text-green-600">{settingsSuccess}</p>}
            <div className="mt-4">
              <Button onClick={saveOrgSettings} disabled={settingsSaving}>
                {settingsSaving ? 'Saving…' : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fortnightly Pay Cycle */}
        <Card>
          <CardHeader>
            <CardTitle>Fortnightly Pay Cycle</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Set a reference start date for fortnightly-paid staff. The system uses this anchor to
              calculate which fortnight a given date falls in — each period begins 14 days after the
              previous one. Fortnightly-paid workers have an overtime threshold of 76 hours per
              fortnight (rather than 38 hours per week). Leave blank if your organisation only uses weekly pay.
            </p>
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fortnightly Cycle Reference Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={fortnightlyRefDate}
                onChange={e => setFortnightlyRefDate(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Pick any date that is the known start of a fortnightly pay period.
                All subsequent and prior fortnights are derived from this anchor.
              </p>
            </div>
            {fortnightlyRefDate && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                Fortnights start every 14 days from <strong>{formatLocalDateInTz(fortnightlyRefDate, orgTimezone, { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
              </div>
            )}
            {settingsError && <p className="mt-3 text-sm text-red-600">{settingsError}</p>}
            {settingsSuccess && <p className="mt-3 text-sm text-green-600">{settingsSuccess}</p>}
            <div className="mt-4">
              <Button onClick={saveOrgSettings} disabled={settingsSaving}>
                {settingsSaving ? 'Saving…' : 'Save Fortnightly Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Organisation Default Schedule */}
        <Card>
          <CardHeader>
            <CardTitle>Organisation Default Work Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Set the default start time, end time, and lunch break for each day of the week.
              Individual users can override these values below.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              With a lunch start time, clock-out deducts only the part of the break someone was
              clocked on for: leaving before lunch, or clocking out over it, costs nothing. Leave it
              blank and the whole break comes off any session over the lunch threshold.
            </p>
            {orgLoading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium text-gray-700 w-32">Day</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-700">Working Day</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-700">Start Time</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-700">End Time</th>
                      <th className="text-left py-2 pr-4 font-medium text-gray-700">Lunch Starts</th>
                      <th className="text-left py-2 font-medium text-gray-700">Lunch Break (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgSchedule.map(day => (
                      <tr key={day.day_of_week} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium text-gray-800">{DAY_NAMES[day.day_of_week]}</td>
                        <td className="py-2 pr-4">
                          <input
                            type="checkbox"
                            checked={day.is_working_day}
                            onChange={e => updateOrgDay(day.day_of_week, 'is_working_day', e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="py-2 pr-4">
                          <input
                            type="time"
                            value={day.start_time}
                            disabled={!day.is_working_day}
                            onChange={e => updateOrgDay(day.day_of_week, 'start_time', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </td>
                        <td className="py-2 pr-4">
                          <input
                            type="time"
                            value={day.end_time}
                            disabled={!day.is_working_day}
                            onChange={e => updateOrgDay(day.day_of_week, 'end_time', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </td>
                        <td className="py-2 pr-4">
                          <input
                            type="time"
                            aria-label={`${DAY_NAMES[day.day_of_week]} lunch starts`}
                            value={day.lunch_start_time ?? ''}
                            disabled={!day.is_working_day}
                            onChange={e => updateOrgDay(day.day_of_week, 'lunch_start_time', e.target.value || null)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </td>
                        <td className="py-2">
                          <input
                            type="number"
                            min={0}
                            max={120}
                            value={day.lunch_duration_minutes}
                            disabled={!day.is_working_day}
                            onChange={e => updateOrgDay(day.day_of_week, 'lunch_duration_minutes', parseInt(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {orgError && <p className="mt-3 text-sm text-red-600">{orgError}</p>}
            {orgSuccess && <p className="mt-3 text-sm text-green-600">{orgSuccess}</p>}
            <div className="mt-4">
              <Button onClick={saveOrgSchedule} disabled={orgSaving || orgLoading}>
                {orgSaving ? 'Saving…' : 'Save Organisation Schedule'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Per-User Schedule Overrides */}
        <Card>
          <CardHeader>
            <CardTitle>User Schedule Override</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Select a user to set individual schedule overrides. Leave a field blank to inherit from the organisation default.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
              <select
                className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleUserSelect}
                defaultValue=""
              >
                <option value="">— choose an employee —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username}
                  </option>
                ))}
              </select>
            </div>

            {selectedUser && (
              <UserScheduleEditor
                key={selectedUser.userId}
                userId={selectedUser.userId}
                userName={selectedUser.userName}
                orgGraceMinutes={graceMinutes}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
